/**
 * overlayLayout.ts —— 统一占位管理器（阶段1 / 方案 B · 设计方案 §3）
 *
 * ## 为什么要有这个模块
 *
 * pod（席位卡）已由 `seatLayout.planRing` + `validateRing` 用「全局占位求解 + 布局期硬不变量」
 * 正确治理；但 pod 之外的**卫星元素**（对手牌背、下注 chip、操作 pill、盲注/庄家 badge）却
 * 散落在 `Table.tsx` 三段各自为政的 `useMemo`（chipDots/actPills/dealer）+ 一套 CSS 边缘锚定
 * （牌背/badge，对 JS 求解器完全不可见）里。两套子系统被设计成走**同一条 pod→底池射线**却
 * **互相看不见对方坐标** → 碰撞是几何必然（详见 `docs/analysis-mobile-layout-rootcause.md`）。
 *
 * 本模块把 pod（planRing 结果作为固定障碍）+ 全部卫星纳入**同一张 occupancy map**，用统一的
 * 「锚点 + 优先级 + 可行域扫描 + 声明式降级」求解，输出确定性 `LayoutSnapshot`（全元素 OBB），
 * 交 `layoutInvariant.assertNoOverlap` 做布局期红线校验。
 *
 * ## 口径（与 seatLayout 同源）
 *
 * - 纯函数、无 DOM、可毫秒级单测；坐标空间 = 毛毡 padding box 左上角原点，x 右、y 下，px。
 * - 底池锚点 P = 中心带中点；席位径向轴 u = (P − pod)/|…|（pod→底池），切向 t = perp(u)。
 * - 牌背 ±6° 旋转**计入 OBB**（相交判定用 OBB/SAT）；容量预留用旋转后 AABB（两口径分离，§3.1）。
 * - **绝不「叠压也优于不显示」**：约束无解时按声明式降级阶梯 D1~D6（§3.2.3），降级路径本身
 *   也必须过 assertNoOverlap；仍无解则隐藏该瞬态卫星（chip/pill），保证快照零重叠。
 */

import type { Point, RingPlan, SeatDensity, Size } from './seatLayout';
import {
  type LayoutPolicy,
  type AttributionPolicy,
  LAYOUT_POLICY_DEFAULT,
  ATTRIBUTION_POLICY_DEFAULT,
  obbSeparation,
  isWhitelisted,
  obbIntersectArea,
  assertAttribution,
} from './layoutInvariant';

/** 定向包围盒：本地轴半宽/半高 + 绕中心旋转角。rot=0 时退化为轴对齐 AABB。 */
export interface OBB {
  cx: number; // 中心 x（层坐标 px）
  cy: number; // 中心 y（层坐标 px）
  hw: number; // 本地 x 轴半宽（未旋转）
  hh: number; // 本地 y 轴半高（未旋转）
  rot: number; // 旋转角（弧度，顺时针正；牌背为正负 0.10453，其余 0）
}

/**
 * 桌面元素种类（与 PRD §6.1 / 设计 R21-R1 对齐）。dealer 归入 badge（role='D'）。
 *
 * ## Round21-M0 新增 6 类关键区（设计 §9.1 R1 / PRD AC-B1）
 * 此前 occupancy 只见 10 类「布局元素」，导致真实公共牌槽/英雄手牌/牌型提示/摊牌文本/席位徽标/
 * 浮层「从未进入度量」——三类结构性恒定遮挡的共同根因（设计 N-1）。M0 把它们升为一等元素：
 * - `boardSlot`：真实公共牌 5 槽（各自独立 OBB，替代粗粒度名义 `board`）——A 类
 * - `heroCard`：英雄手牌逐张——B 类
 * - `heroCat`：英雄牌型提示（沿用，但由 `Table` **真实注入**）——C 类
 * - `seatMade`：摊牌牌型文本 `.seat__made`——D 类
 * - `seatBadges`：席位徽标 `.seat__badges`（主动溢出卡外）——E 类
 * - `panel`：浮层/页脚面板（结算/评分/赢率/抽屉/模态等，替代把 blockers 塞进 heroExclusion）——F 类
 *
 * 这 6 类**不参与** `assertNoOverlap`（旧口径布局期红线不回退，见 layoutInvariant.PROTECTED_REGION_KINDS
 * 跳过表），只由新的 `assertProtectedRegions` 按各自保护强度判定 visFrac / 侵入面积。
 */
export type ElementKind =
  | 'pod' // 席位卡（含「我」）
  | 'cardback' // 对手牌背（双牌扇形，正负 6° 旋转）
  | 'chip' // 下注筹码标签
  | 'pill' // 操作提示 pill
  | 'badge' // 盲注 SB/BB 或庄家 D（role 区分）
  | 'pot' // 底池标签
  | 'board' // 公共牌（粗粒度并集；真实槽见 boardSlot）
  | 'heroCat' // 英雄牌型提示（C 类关键区）
  | 'centerBand' // 受保护中心带（board+pot 的并集包络）
  | 'heroExclusion' // 英雄排他区（「我」的 pod + 手牌保护矩形）
  // ---- Round21-M0 新增 6 类关键区（A~F）----
  | 'boardSlot' // A：真实公共牌单槽（0..4）
  | 'heroCard' // B：英雄手牌单张（0..1）
  | 'seatMade' // D：摊牌牌型文本 .seat__made
  | 'seatBadges' // E：席位徽标 .seat__badges（外溢盒）
  | 'panel'; // F：浮层/页脚面板（结算/评分/赢率/抽屉/模态）

/**
 * `ElementKind` 的运行时枚举（单一真源，供 AC-G6「文档 ElementKind 表 ↔ 代码枚举逐项一致」用例断言）。
 * 顺序 = 设计 `design-layout-refactor.md` §3.1 元素清单表的行序。新增 kind 必须同步此数组与文档表。
 */
export const ELEMENT_KINDS = [
  'pod',
  'cardback',
  'chip',
  'pill',
  'badge',
  'pot',
  'board',
  'heroCat',
  'centerBand',
  'heroExclusion',
  'boardSlot',
  'heroCard',
  'seatMade',
  'seatBadges',
  'panel',
] as const satisfies readonly ElementKind[];

// 编译期完备性：若 ElementKind 新增/删减但未同步 ELEMENT_KINDS，下面赋值会类型报错。
type _ElementKindExhaustive = Exclude<ElementKind, (typeof ELEMENT_KINDS)[number]>;
const _elementKindExhaustive: _ElementKindExhaustive extends never ? true : never = true;
void _elementKindExhaustive;

export interface LayoutElement {
  /** 唯一标识：`seat3.cardback`/`seat3.chip`/`dealer`/`pot`/`board`/`centerBand`/`hero.exclusion` 等。 */
  id: string;
  kind: ElementKind;
  /** 卫星归属其席位号；无归属（pot/board/centerBand）为 null。 */
  seat: number | null;
  /** 仅 kind==='badge' 时区分（SB/BB/D）。 */
  role?: 'SB' | 'BB' | 'D';
  obb: OBB;
  /** 平面 A z 值（来自 --z-* token），仅用于绘序/诊断，不参与相交判定。 */
  z: number;
  /** 降级动作诊断（如 'shrink'/'merge'/'rot0'/'hidden'），无降级为 undefined。 */
  degraded?: string;
  /**
   * 归属度 κ（Round21-M0 预留字段，M2 归属重构落地）：卫星相对其主人 pod 的归属强度。
   * M0 仅在快照上占位（可空），`assertAttribution` 桩函数读取；不影响 assertNoOverlap。
   */
  kappa?: number;
  /**
   * Round21-M2：该 pod 是否已淘汰（AC-A8）。淘汰席位既不作为卫星主人，也不作为 `d_nearest_other`
   * 候选。仅 kind==='pod' 有意义；缺省 false。
   */
  eliminated?: boolean;
  /**
   * Round21-M2：inPod 承载时该卫星所属的 pod 内槽（P3 动作条 / P4 牌背小徽），供渲染层区分。
   */
  podSlot?: 'P3' | 'P4' | 'P5';
}

/** 布局纯函数在给定 {box, n, phase} 下输出的确定性快照。 */
export interface LayoutSnapshot {
  box: { w: number; h: number };
  density: SeatDensity;
  /** 全部桌面元素 OBB（pod + 全部卫星 + 保护区 + Round21-M0 的 6 类关键区）。 */
  elements: LayoutElement[];
  /** 降级/隐藏记录（诊断用），如 `['seat7.pill:hidden']`。 */
  degradations: string[];
  /**
   * 英雄动作承载模式（Round21-M0 预留字段，M2 `carrierOf()` 落地）：
   * `'external'` = 英雄区外置卫星；`'inPod'` = 进英雄 pod 的 P3 动作条。M0 可空。
   */
  carrier?: 'external' | 'inPod';
}

/** z 平面 A token 值（tokens.css L51-58；仅绘序/诊断/关键区遮挡层静态偏序，不参与相交判定）。 */
export const Z = {
  felt: 0,
  seat: 1,
  bubble: 4,
  seatBadge: 5,
  seatPop: 30,
  board: 40,
  /** 浮层平面（对应 CSS `.eqshell` 等 z-index:110；关键区遮挡层偏序用，设计 N-6）。 */
  panel: 110,
} as const;

/** 单档卫星尺寸表（未旋转本地盒，px）。牌背旋转后 AABB 半extent另给（容量预留用）。 */
export interface SatelliteSizeTable {
  cardback: Size; // 双牌 + 2gap
  chip: Size;
  pill: Size;
  badge: Size; // SB/BB
  dealer: Size; // 庄家 D
  mergedPill: Size; // 复合 pill（合并降级 D2）
  /** 牌背旋转后 AABB 半extent（±6° 计入，供容量预留）。 */
  cardbackRotHalf: { hw: number; hh: number };
  // ---- Round21-M2 新增（设计 §4.2.3 承载矩阵 / §4.2.2 C1~C3）----
  /** C1 合并标签「● 动作 金额」（chip+pill 并一行，radial/tangential 承载）。 */
  merged: Size;
  /** P4 牌背小徽（2×mini 牌，inPod 承载，rot=0，桌心侧上角）。 */
  cardbackMini: Size;
  /** P3 内嵌动作条（inPod 承载，宽=pod 内宽−4 动态取，此为标称下限盒）。 */
  actionbarInPod: Size;
}

/** 径向间隙序列（px）。 */
export interface GapTable {
  g0: number; // pod→cardback
  g1: number; // cardback→chip
  g2: number; // chip→pill
  tStep: number; // 切向错位步
  K: number; // 切向最大档
}

/** 旋转三角常量（cos6°/sin6°），牌背 ±6° = ±0.10453 rad（设计 §2）。 */
export const CARD_ROT = 0.10453;
const COS6 = 0.99452;
const SIN6 = 0.10453;

/**
 * 卫星尺寸密度联动表（设计 §6，唯一数字来源）。相较现状硬编码 chip hw30/pill hw46、
 * 且牌背旋转从未计入 —— 本表全部随密度档联动、牌背旋转计入旋转后 AABB。
 */
export const SAT_SIZES: Record<SeatDensity, SatelliteSizeTable> = {
  full: {
    cardback: { w: 42, h: 28 },
    chip: { w: 56, h: 22 },
    pill: { w: 92, h: 20 },
    badge: { w: 36, h: 18 }, // 「小盲/大盲」中文双字 + padding，与 .felt__blindbadge 渲染盒一致
    dealer: { w: 18, h: 18 },
    mergedPill: { w: 100, h: 22 },
    cardbackRotHalf: { hw: 21 * COS6 + 14 * SIN6, hh: 21 * SIN6 + 14 * COS6 },
    merged: { w: 100, h: 22 }, // S0 合并标签
    cardbackMini: { w: 26, h: 18 }, // S0 P4 小徽
    actionbarInPod: { w: 70, h: 16 }, // S0 P3 内嵌动作条标称
  },
  compact: {
    cardback: { w: 38, h: 25 },
    chip: { w: 52, h: 20 },
    pill: { w: 84, h: 18 },
    badge: { w: 32, h: 16 },
    dealer: { w: 16, h: 16 },
    mergedPill: { w: 92, h: 20 },
    cardbackRotHalf: { hw: 19 * COS6 + 12.5 * SIN6, hh: 19 * SIN6 + 12.5 * COS6 },
    merged: { w: 92, h: 20 }, // S1 合并标签
    cardbackMini: { w: 24, h: 16 }, // S1 P4 小徽
    actionbarInPod: { w: 66, h: 15 }, // S1 P3 内嵌动作条
  },
  row: {
    cardback: { w: 34, h: 22 },
    chip: { w: 46, h: 18 },
    pill: { w: 72, h: 16 },
    badge: { w: 32, h: 16 },
    dealer: { w: 16, h: 16 },
    mergedPill: { w: 80, h: 18 },
    cardbackRotHalf: { hw: 17 * COS6 + 11 * SIN6, hh: 17 * SIN6 + 11 * COS6 },
    merged: { w: 80, h: 18 }, // S2 合并标签
    cardbackMini: { w: 22, h: 15 }, // S2 P4 小徽
    actionbarInPod: { w: 60, h: 14 }, // S2 P3 内嵌动作条
  },
  micro: {
    cardback: { w: 30, h: 20 },
    chip: { w: 40, h: 16 },
    pill: { w: 60, h: 14 },
    badge: { w: 30, h: 16 },
    dealer: { w: 14, h: 14 },
    mergedPill: { w: 68, h: 16 },
    cardbackRotHalf: { hw: 15 * COS6 + 10 * SIN6, hh: 15 * SIN6 + 10 * COS6 },
    merged: { w: 68, h: 16 }, // S3/S4 合并标签（外置时）
    cardbackMini: { w: 20, h: 14 }, // S3/S4 P4 小徽（20×14）
    actionbarInPod: { w: 60, h: 14 }, // S3/S4 P3 内嵌动作条（≥60 宽，放得下「加注 497」）
  },
};

/** 径向间隙表（设计 §6）。 */
export const GAPS: Record<SeatDensity, GapTable> = {
  full: { g0: 4, g1: 6, g2: 5, tStep: 22, K: 3 },
  compact: { g0: 4, g1: 5, g2: 4, tStep: 20, K: 3 },
  row: { g0: 3, g1: 4, g2: 4, tStep: 18, K: 2 },
  micro: { g0: 2, g1: 3, g2: 3, tStep: 16, K: 1 },
};

/** 每对手运行态（决定其有哪些卫星、卫星归属方向）。 */
export interface SeatRuntime {
  seat: number;
  pod: OBB; // pod 中心 OBB（来自 ring.seats[idx]）
  innerSide: 'left' | 'right' | 'bottom';
  inHand: boolean;
  folded: boolean;
  /**
   * Round25 · C2：单机全揭示态（`revealAll`，仅 handComplete/gameOver）。为 true 时**已弃牌者也产出
   * 牌背槽位**，摊牌 face-up 亮牌由「统一 solver 锚点」承载（§4.2.3），彻底删除 Table 层独立放大扇形。
   * 联机恒 false（`MpTable` 不走本路径），向后兼容缺省 false。
   */
  revealAll?: boolean;
  streetContributed: number; // >0 才有 chip
  chipAmountText?: string; // chip 文案（诊断）
  actionKind?: 'fold' | 'check' | 'call' | 'raise' | 'bet' | 'allin' | null; // 有值才有 pill
  pillText?: string; // pill 文案（诊断）
  pillCls?: string; // pill 配色类（诊断）
  blind?: 'SB' | 'BB' | null;
  isDealer: boolean;
  /** Round21-M2（AC-A8）：该席已淘汰（离桌）——不参与归属承载与 `d_nearest_other` 候选。 */
  eliminated?: boolean;
  /**
   * Round21-M0 关键区（可空，由 Table 实测注入；缺省则该席不产出对应关键区元素）：
   * `made` = 摊牌牌型文本 `.seat__made` 实测 OBB（D 类）；
   * `badges` = 席位徽标 `.seat__badges` 实测 OBB（E 类，主动溢出卡外）。
   */
  made?: OBB;
  badges?: OBB;
}

export interface OverlayInput {
  box: Size;
  ring: RingPlan; // planRing 结果：pod 中心 + 密度档 + card 盒
  pot: Point; // = P（中心带中点）
  band: { top: number; bottom: number; halfWidth: number }; // 受保护中心带
  /**
   * 英雄排他区构件。Round21-M0：`cat` = 牌型提示（C 类，必须真实注入）；
   * `cards` = 英雄手牌逐张 OBB（B 类，通常 2 张）。缺省则不产出对应关键区（旧口径向后兼容）。
   */
  hero: { pod: OBB; cat?: OBB; cards?: OBB[] };
  seats: SeatRuntime[]; // 每对手运行态
  sizes?: SatelliteSizeTable; // 当前密度档卫星尺寸（缺省按 ring.density 取 SAT_SIZES）
  gaps?: GapTable;
  policy?: LayoutPolicy;
  blockers?: OBB[]; // 外部浮层（赢率/风格卡）——solver 障碍，M0 口径不变
  /**
   * Round21-M0 浮层/页脚面板实测 OBB（F 类关键区，`panel`）：结算/评分/赢率/抽屉/模态等。
   * **仅供 `assertProtectedRegions` 度量**（不进 solver occupancy → 不改 M0 solver 观感/目标函数）。
   */
  boardSlots?: OBB[]; // A 类：真实公共牌 5 槽实测 OBB（替代名义 board）
  panels?: OBB[]; // F 类：浮层/页脚面板实测 OBB
}

// ---------------------------------------------------------------------------
// 几何小工具（层坐标）
// ---------------------------------------------------------------------------

const EPS = 0.01;

export function unit(dx: number, dy: number): Point {
  const len = Math.hypot(dx, dy) || 1;
  return { x: dx / len, y: dy / len };
}

/** 切向：右手系 perp（−u.y, u.x）。 */
export function perp(u: Point): Point {
  return { x: -u.y, y: u.x };
}

/** 矩形（半宽 hw、半高 hh，未旋转）沿单位向量 u 的支撑半径（chipDots/dealer 现用口径）。 */
export function proj(hw: number, hh: number, u: Point): number {
  return Math.abs(u.x) * hw + Math.abs(u.y) * hh;
}

export function obbAt(cx: number, cy: number, hw: number, hh: number, rot: number): OBB {
  return { cx, cy, hw, hh, rot };
}

/** OBB 的轴对齐外接盒是否越出内容盒（用旋转后 AABB 半extent）。 */
function outOfBox(o: OBB, box: Size): boolean {
  const c = Math.abs(Math.cos(o.rot));
  const s = Math.abs(Math.sin(o.rot));
  const hw = o.hw * c + o.hh * s;
  const hh = o.hw * s + o.hh * c;
  return o.cx - hw < -EPS || o.cy - hh < -EPS || o.cx + hw > box.w + EPS || o.cy + hh > box.h + EPS;
}

// ---------------------------------------------------------------------------
// Occupancy：统一占位表
// ---------------------------------------------------------------------------

/**
 * 统一占位表：固定障碍（pod/中心带/英雄区/board/pot/浮层，优先级 ∞）+ 逐步放入的卫星。
 * `freeFor` 用与 assertNoOverlap 同源的 OBB/SAT 相交面积口径（含白名单），保证求解产出
 * 天然能过不变量。
 */
export class Occupancy {
  readonly fixed: LayoutElement[] = [];
  readonly placed: LayoutElement[] = [];
  private readonly policy: LayoutPolicy;
  private readonly box: Size;
  private readonly bandTop: number;
  private readonly bandBottom: number;
  private readonly bandHalf: number;
  private readonly cx: number;

  constructor(box: Size, band: { top: number; bottom: number; halfWidth: number }, policy: LayoutPolicy) {
    this.box = box;
    this.policy = policy;
    this.bandTop = band.top;
    this.bandBottom = band.bottom;
    this.bandHalf = band.halfWidth;
    this.cx = box.w / 2;
  }

  addFixed(el: LayoutElement): void {
    this.fixed.push(el);
  }

  add(el: LayoutElement): void {
    this.placed.push(el);
  }

  /** 候选卫星（未加入的临时元素）与所有已存元素是否可共存（无非法相交、不越界、不侵中心带）。 */
  freeFor(cand: LayoutElement): boolean {
    if (outOfBox(cand.obb, this.box)) return false;
    // 中心带间隙：卫星到中心带 ≥ centerBandGap（把中心带按 gap 膨胀后判分离）。
    const gap = this.policy.centerBandGap;
    const bandObb = obbAt(
      this.cx,
      (this.bandTop + this.bandBottom) / 2,
      this.bandHalf + gap,
      (this.bandBottom - this.bandTop) / 2 + gap,
      0,
    );
    const sepBand = obbSeparation(cand.obb, bandObb);
    if (sepBand.overlap) return false;
    // 与所有固定 + 已放元素两两校验（白名单口径与 assertNoOverlap 一致）。
    for (const other of this.iter()) {
      if (other.kind === 'centerBand' || other.kind === 'board' || other.kind === 'pot') continue;
      const sep = obbSeparation(cand.obb, other.obb);
      if (!sep.overlap) continue;
      const wl = isWhitelisted(cand, other);
      if (wl) {
        const area = obbIntersectArea(cand.obb, other.obb);
        const refArea = wl.refKind === 'a' ? area4(cand.obb) : area4(other.obb);
        if (area > wl.maxAreaFrac * refArea + EPS) return false;
        if (sep.depth > wl.maxPenetration + EPS) return false;
      } else {
        // 非白名单：必须真正分离（相交面积 ≤ eps）。留 0.5px 安全净空避免边界抖动。
        const area = obbIntersectArea(cand.obb, other.obb);
        if (area > EPS) return false;
        if (sep.depth > 0.5) return false;
      }
    }
    return true;
  }

  private *iter(): Generator<LayoutElement> {
    yield* this.fixed;
    yield* this.placed;
  }
}

function area4(o: OBB): number {
  return 4 * o.hw * o.hh;
}

// ---------------------------------------------------------------------------
// 求解主流程
// ---------------------------------------------------------------------------

/**
 * 阶段1/阶段2 共用的**固定障碍装配**（单一真源）：把 pod（对手 + 我）/ 英雄排他区 / 中心带 /
 * board / pot / heroCat / 外部浮层一次性纳入 occupancy 与快照元素表。solveOverlayLayout（阶段1
 * 求解）与 seatLayout.clusterPlanToSnapshot（阶段2 模板落位）都调用它，保证「固定障碍口径」永不漂移。
 */
export interface FixedSetup {
  occ: Occupancy;
  elements: LayoutElement[];
  degradations: string[];
  density: SeatDensity;
  sizes: SatelliteSizeTable;
  gaps: GapTable;
  policy: LayoutPolicy;
  P: Point;
  box: Size;
}

export function initFixed(inp: OverlayInput): FixedSetup {
  const density = inp.ring.density;
  const sizes = inp.sizes ?? SAT_SIZES[density];
  const gaps = inp.gaps ?? GAPS[density];
  const policy = inp.policy ?? LAYOUT_POLICY_DEFAULT;
  const box = inp.box;
  const P = inp.pot;

  const occ = new Occupancy(box, inp.band, policy);
  const elements: LayoutElement[] = [];
  const degradations: string[] = [];

  // 固定障碍进 occupancy。
  for (const s of inp.seats) {
    const el: LayoutElement = { id: `seat${s.seat}.pod`, kind: 'pod', seat: s.seat, obb: s.pod, z: Z.seat };
    occ.addFixed(el);
    elements.push(el);
  }
  const heroPodEl: LayoutElement = { id: 'hero.pod', kind: 'pod', seat: HUMAN_SEAT, obb: inp.hero.pod, z: Z.seat };
  occ.addFixed(heroPodEl);
  elements.push(heroPodEl);

  const heroExcl: LayoutElement = {
    id: 'hero.exclusion',
    kind: 'heroExclusion',
    seat: HUMAN_SEAT,
    obb: growObb(inp.hero.pod, 4),
    z: Z.seat,
  };
  occ.addFixed(heroExcl);
  elements.push(heroExcl);

  const bandCy = (inp.band.top + inp.band.bottom) / 2;

  // ---- A 类关键区：真实公共牌 5 槽（设计 §4.4.3 / AC-B1/AC-B4）----
  // 提供实测槽时：centerBand 由 5 槽并集 ⊕ 呼吸带包络给出（AC-B4：不再用硬编码半高 20/半宽封顶 90 的
  // 名义 board OBB）；否则回退旧口径（纯函数向后兼容，既有 307 用例不受影响）。
  let centerObb: OBB;
  let boardObb: OBB;
  if (inp.boardSlots && inp.boardSlots.length > 0) {
    const u = unionAabb(inp.boardSlots);
    boardObb = aabbToObb(u);
    // centerBand = union(boardSlot0..4) 的并集包络（AC-B4：与真实并集各边偏差 ≤2px，此处取 0 偏差）。
    // 保护呼吸带（slot ⊕ boardSlotGap）在 assertProtectedRegions 判定时施加，不烘焙进 centerBand OBB。
    centerObb = aabbToObb(u);
    inp.boardSlots.forEach((slot, i) => {
      elements.push({ id: `boardSlot${i}`, kind: 'boardSlot', seat: null, obb: slot, z: Z.board });
    });
  } else {
    centerObb = obbAt(box.w / 2, bandCy, inp.band.halfWidth, (inp.band.bottom - inp.band.top) / 2, 0);
    boardObb = obbAt(box.w / 2, inp.band.top + 22, Math.min(inp.band.halfWidth, 90), 20, 0);
  }
  const centerBandEl: LayoutElement = { id: 'centerBand', kind: 'centerBand', seat: null, obb: centerObb, z: Z.board };
  elements.push(centerBandEl);
  // board / pot 作为诊断元素（落在中心带内），不作为放置障碍（中心带已代表）。
  elements.push({ id: 'board', kind: 'board', seat: null, obb: boardObb, z: Z.board });
  elements.push({ id: 'pot', kind: 'pot', seat: null, obb: obbAt(box.w / 2, inp.band.bottom - 12, 34, 10, 0), z: Z.board });

  // ---- C 类关键区：英雄牌型提示 hero.cat（设计 §4.4.4，必须由 Table 真实注入）----
  // M0 仅做度量：不 addFixed 进 occupancy（保持 solver 观感/目标函数不变；进 occupancy 是 M1/M2 的事）。
  if (inp.hero.cat) {
    elements.push({ id: 'hero.cat', kind: 'heroCat', seat: HUMAN_SEAT, obb: inp.hero.cat, z: Z.seat });
  }

  // ---- B 类关键区：英雄手牌逐张（设计 §4.4.4 / AC-B1）----
  if (inp.hero.cards) {
    inp.hero.cards.forEach((card, i) => {
      elements.push({ id: `hero.card${i}`, kind: 'heroCard', seat: HUMAN_SEAT, obb: card, z: Z.seat });
    });
  }

  // ---- D/E 类关键区：逐席 .seat__made / .seat__badges（设计 §4.4.2）----
  for (const s of inp.seats) {
    if (s.made) elements.push({ id: `seat${s.seat}.made`, kind: 'seatMade', seat: s.seat, obb: s.made, z: Z.seat });
    if (s.badges) elements.push({ id: `seat${s.seat}.badges`, kind: 'seatBadges', seat: s.seat, obb: s.badges, z: Z.seatBadge });
  }

  // ---- F 类关键区：浮层/页脚面板（设计 §4.6，仅供度量，不进 occupancy）----
  if (inp.panels) {
    inp.panels.forEach((p, i) => {
      elements.push({ id: `panel${i}`, kind: 'panel', seat: null, obb: p, z: Z.panel });
    });
  }

  // 旧口径外部浮层（solver 障碍，M0 不改）：仍以 heroExclusion 语义进 occupancy，供 solver 避让。
  for (let i = 0; i < (inp.blockers?.length ?? 0); i++) {
    const b = inp.blockers![i];
    const el: LayoutElement = { id: `blocker${i}`, kind: 'heroExclusion', seat: null, obb: b, z: Z.seatPop };
    occ.addFixed(el);
    elements.push(el);
  }

  return { occ, elements, degradations, density, sizes, gaps, policy, P, box };
}

/** 轴对齐外接盒（用旋转后 AABB 半extent），供关键区并集包络。 */
interface Aabb {
  cx: number;
  cy: number;
  hw: number;
  hh: number;
}

function aabbOf(o: OBB): Aabb {
  const c = Math.abs(Math.cos(o.rot));
  const s = Math.abs(Math.sin(o.rot));
  return { cx: o.cx, cy: o.cy, hw: o.hw * c + o.hh * s, hh: o.hw * s + o.hh * c };
}

/** 一组 OBB 的轴对齐并集包络（AC-B4：真实 5 槽 → centerBand 包络）。 */
function unionAabb(obbs: OBB[]): Aabb {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const o of obbs) {
    const a = aabbOf(o);
    minX = Math.min(minX, a.cx - a.hw);
    minY = Math.min(minY, a.cy - a.hh);
    maxX = Math.max(maxX, a.cx + a.hw);
    maxY = Math.max(maxY, a.cy + a.hh);
  }
  return { cx: (minX + maxX) / 2, cy: (minY + maxY) / 2, hw: (maxX - minX) / 2, hh: (maxY - minY) / 2 };
}

function aabbToObb(a: Aabb): OBB {
  return obbAt(a.cx, a.cy, a.hw, a.hh, 0);
}

/**
 * 统一求解：pod 作为固定障碍，所有卫星入同一 occupancy map；按优先级放置，冲突走可行域扫描，
 * 无解走声明式降级阶梯，绝不叠压。返回确定性 LayoutSnapshot。
 */
export function solveOverlayLayout(inp: OverlayInput): LayoutSnapshot {
  const { occ, elements, degradations, density, sizes, gaps, P, box } = initFixed(inp);

  // 2. 卫星按「元素优先级」放置：badge/D > cardback > chip > pill（高优先者先占理想槽）。
  //    席位优先级：先近底池短射线者（空间紧张者先占）。
  const orderedSeats = [...inp.seats].sort((a, b) => radialFree(a, P) - radialFree(b, P));

  for (const s of orderedSeats) {
    const u = unit(P.x - s.pod.cx, P.y - s.pod.cy);
    const t = perp(u);
    const podEdge = proj(s.pod.hw, s.pod.hh, u);

    // 2a. badge/D：pod 角槽（切向偏移，不占径向轨道）。SB/BB 与 D 分列 pod 两角。
    placeBadges(s, u, t, sizes, occ, elements, degradations);

    // 2b. cardback：贴 pod 近缘（inHand 且「未弃牌或全揭示」才有，Round25 C2）。短射线（侧列中位贴中心带）走角向扫掠
    //     绕 pod 朝底池半弧找位（对应现状 CSS 左列 +6°/右列 −6° 从 pod 边缘引出）。
    let reservedR = podEdge; // 已占用的自 pod 中心的外缘半径（供 chip/pill 链式外推）
    if (s.inHand && (!s.folded || s.revealAll)) {
      const rot = rotOf(s.innerSide);
      const rotHalf = sizes.cardbackRotHalf;
      const dCard = podEdge + gaps.g0 + proj(rotHalf.hw, rotHalf.hh, u);
      const res = placeSatellite(s, u, dCard, sizes.cardback.w / 2, sizes.cardback.h / 2, rot, occ, {
        id: `seat${s.seat}.cardback`,
        kind: 'cardback',
        seat: s.seat,
        z: Z.seat,
      });
      if (res.el) {
        elements.push(res.el);
        reservedR = res.farR;
      } else {
        // D3：牌背 rot→0（AABB 更小）再试。
        const res0 = placeSatellite(s, u, podEdge + gaps.g0 + sizes.cardback.h / 2, sizes.cardback.w / 2, sizes.cardback.h / 2, 0, occ, {
          id: `seat${s.seat}.cardback`,
          kind: 'cardback',
          seat: s.seat,
          z: Z.seat,
          degraded: 'rot0',
        });
        if (res0.el) {
          elements.push(res0.el);
          reservedR = res0.farR;
          degradations.push(`seat${s.seat}.cardback:rot0`);
        } else {
          degradations.push(`seat${s.seat}.cardback:hidden`);
        }
      }
    }

    // 2c. chip：越过 cardback 向底池（streetContributed>0 才有）。
    let chipPlaced = false;
    if (s.streetContributed > 0) {
      const dChip = reservedR + gaps.g1 + proj(sizes.chip.w / 2, sizes.chip.h / 2, u);
      const res = placeSatellite(s, u, dChip, sizes.chip.w / 2, sizes.chip.h / 2, 0, occ, {
        id: `seat${s.seat}.chip`,
        kind: 'chip',
        seat: s.seat,
        z: Z.bubble,
      });
      if (res.el) {
        elements.push(res.el);
        reservedR = res.farR;
        chipPlaced = true;
      } else {
        // D1：chip 缩到下一档尺寸再试。
        const sm = nextSize(density, 'chip');
        const res2 = placeSatellite(s, u, reservedR + gaps.g1 + proj(sm.w / 2, sm.h / 2, u), sm.w / 2, sm.h / 2, 0, occ, {
          id: `seat${s.seat}.chip`,
          kind: 'chip',
          seat: s.seat,
          z: Z.bubble,
          degraded: 'shrink',
        });
        if (res2.el) {
          elements.push(res2.el);
          reservedR = res2.farR;
          chipPlaced = true;
          degradations.push(`seat${s.seat}.chip:shrink`);
        } else {
          degradations.push(`seat${s.seat}.chip:hidden`);
        }
      }
    }

    // 2d. pill：越过 chip 向底池（有 actionKind 才有）。
    if (s.actionKind) {
      const dPill = reservedR + gaps.g2 + proj(sizes.pill.w / 2, sizes.pill.h / 2, u);
      const res = placeSatellite(s, u, dPill, sizes.pill.w / 2, sizes.pill.h / 2, 0, occ, {
        id: `seat${s.seat}.pill`,
        kind: 'pill',
        seat: s.seat,
        z: Z.bubble,
      });
      if (res.el) {
        elements.push(res.el);
      } else {
        // D2：chip+pill 合并为复合 pill（径向占一个槽）。
        let merged = false;
        if (chipPlaced) {
          const mp = sizes.mergedPill;
          const chipIdx = occ.placed.findIndex((e) => e.id === `seat${s.seat}.chip`);
          let removedFarBase = podEdge;
          if (chipIdx >= 0) {
            const removed = occ.placed.splice(chipIdx, 1)[0];
            const remIdx = elements.findIndex((e) => e.id === removed.id);
            if (remIdx >= 0) elements.splice(remIdx, 1);
            removedFarBase = Math.hypot(removed.obb.cx - s.pod.cx, removed.obb.cy - s.pod.cy) - proj(removed.obb.hw, removed.obb.hh, u);
          }
          const dm = Math.max(podEdge, removedFarBase) + gaps.g1 + proj(mp.w / 2, mp.h / 2, u);
          const resM = placeSatellite(s, u, dm, mp.w / 2, mp.h / 2, 0, occ, {
            id: `seat${s.seat}.pill`,
            kind: 'pill',
            seat: s.seat,
            z: Z.bubble,
            degraded: 'merge',
          });
          if (resM.el) {
            elements.push(resM.el);
            merged = true;
            degradations.push(`seat${s.seat}.chip+pill:merge`);
          }
        }
        if (!merged) {
          // D1：pill 缩档再试。
          const sm = nextSize(density, 'pill');
          const res2 = placeSatellite(s, u, reservedR + gaps.g2 + proj(sm.w / 2, sm.h / 2, u), sm.w / 2, sm.h / 2, 0, occ, {
            id: `seat${s.seat}.pill`,
            kind: 'pill',
            seat: s.seat,
            z: Z.bubble,
            degraded: 'shrink',
          });
          if (res2.el) {
            elements.push(res2.el);
            degradations.push(`seat${s.seat}.pill:shrink`);
          } else {
            /*
             * Round25 · M2.1 / C3（round25-pill-must-visible）：行动 pill 硬可见，**禁止合规隐藏**。
             * 穷尽缩档仍无常规解时，强制落在头像内侧 pod 上缘（同主人、贴自身 pod），保证 visFrac>0；
             * 记 `pill:forced`（可诊断），绝不 `pill:hidden`（废除根因问题5 的隐藏分支）。
             */
            const sm2 = nextSize(density, 'pill');
            const inPodCy = s.pod.cy - s.pod.hh + 0.16 * 2 * s.pod.hh;
            const forced: LayoutElement = {
              id: `seat${s.seat}.pill`,
              kind: 'pill',
              seat: s.seat,
              z: Z.bubble,
              degraded: 'inPod',
              obb: obbAt(s.pod.cx, inPodCy, sm2.w / 2, sm2.h / 2, 0),
            };
            occ.add(forced);
            elements.push(forced);
            degradations.push(`seat${s.seat}.pill:forced`);
          }
        }
      }
    }
  }

  // 注：M1 求解器保持原样（阶段1 稳定回退基座，不改观感/目标）。R21-M2 归属护栏
  // `enforceAttribution` 作为**回退路径的收敛后处理**由调用方（Table 回退分支）按需施加，
  // 不烘焙进求解器本身——避免误删 M1 的 ±6° 侧列牌背等既有红线行为。主路径 `clusterPlanToSnapshot`
  // 在落位时即逐候选校验归属，无需后处理。
  return { box, density, elements, degradations };
}

/** 卫星删除优先级（收敛=隐藏）：pill 最先删，badge 最后删（保留结构标记）。 */
function removalPriority(kind: ElementKind): number {
  return kind === 'pill' ? 0 : kind === 'chip' ? 1 : kind === 'cardback' ? 2 : 3;
}

/**
 * 归属护栏（R21-M2 · AC-A1~A3）：**就地**删除仍违反 `assertAttribution` 的瞬态卫星，直至全绿。
 *
 * 用于 M1 回退求解器（`solveOverlayLayout`）等可能产出「归属歧义」卫星的路径：逐轮取当前违例涉及的
 * 卫星，按 `removalPriority`（pill > chip > cardback > badge）删除代价最小者并记 `:hidden`，再复核。
 * 这是「信息收敛 = 隐藏可诊断」原则在回退路径上的落地——**绝不叠压、绝不放宽 κ**。cluster 主路径
 * （`clusterPlanToSnapshot`）在落位时即逐候选校验归属，通常无需本护栏兜底。
 */
export function enforceAttribution(
  elements: LayoutElement[],
  degradations: string[],
  box: Size,
  density: SeatDensity,
): void {
  for (let iter = 0; iter <= elements.length + 4; iter++) {
    const res = assertAttribution({ box, density, elements, degradations });
    if (res.ok) return;
    const ids = new Set<string>();
    for (const v of res.violations) {
      const m = v.match(/#(\S+)/g);
      if (m) for (const tok of m) ids.add(tok.slice(1));
    }
    // Round25 · M2.1（round25-pill-must-visible）：行动 pill 是**硬可见约束**，不作为删除对象。
    // 若归属护栏认定某 pill 违例（κ 歧义/贴靠超限），**就地重定位到本座 pod 上缘标识带**（inPod）——
    // 落在自身 pod 中心 → 归属唯一（κ→0、edgeGapOwn→0）、跨主人间距天然达标，既保可见又解歧义，绝不隐藏。
    const pillViolators = elements.filter(
      (e) => ids.has(e.id) && e.kind === 'pill' && e.seat !== null && e.degraded !== 'inPod',
    );
    if (pillViolators.length > 0) {
      let relocated = false;
      for (const pill of pillViolators) {
        const pod = elements.find((e) => e.kind === 'pod' && e.seat === pill.seat && !e.eliminated);
        if (!pod) continue;
        const inPodCy = pod.obb.cy - pod.obb.hh + 0.16 * 2 * pod.obb.hh;
        pill.obb = obbAt(pod.obb.cx, inPodCy, pill.obb.hw, pill.obb.hh, 0);
        pill.degraded = 'inPod';
        if (!degradations.includes(`${pill.id}:forced`)) degradations.push(`${pill.id}:forced`);
        relocated = true;
      }
      if (relocated) continue; // 重新评估归属
    }
    const victims = elements.filter(
      // Round25 · M2.1：行动 pill 永不删（上面已重定位）；仅可删 chip/cardback/badge。
      (e) => ids.has(e.id) && (e.kind === 'chip' || e.kind === 'cardback' || e.kind === 'badge'),
    );
    if (victims.length === 0) return; // 无可删卫星（理论不达）——避免死循环
    victims.sort((a, b) => removalPriority(a.kind) - removalPriority(b.kind));
    const victim = victims[0];
    const idx = elements.findIndex((e) => e.id === victim.id);
    if (idx >= 0) elements.splice(idx, 1);
    if (!degradations.includes(`${victim.id}:hidden`)) degradations.push(`${victim.id}:hidden`);
  }
}

/** HUMAN 席位号（引擎恒为 0）。 */
export const HUMAN_SEAT = 0;

// ---------------------------------------------------------------------------
// Round21-M1 · 英雄动作承载 + 浮层让位（T3 · AC-B3-① / AC-B6 的结构性根治）
// ---------------------------------------------------------------------------

/** 一组 OBB 的轴对齐并集（层坐标，含旋转后 AABB）。供英雄手牌并集/公共牌带并集使用。 */
export function unionOf(obbs: OBB[]): { left: number; top: number; right: number; bottom: number } {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const o of obbs) {
    const a = aabbOf(o);
    left = Math.min(left, a.cx - a.hw);
    top = Math.min(top, a.cy - a.hh);
    right = Math.max(right, a.cx + a.hw);
    bottom = Math.max(bottom, a.cy + a.hh);
  }
  return { left, top, right, bottom };
}

/**
 * 英雄动作气泡（合并标签）承载（设计 §4.4.4，Round21-M1 · AC-B3-①）。
 *
 * 现状 `.bubble{top:2;right:2}` 被 CSS 钉在英雄 pod 右上角 → 与英雄第 2 张手牌**恒定相交 143.4px²**。
 * 本函数把英雄动作改由布局层给坐标：**优先落在英雄手牌下方**（§4.7 回收出的死区，`cards.bottom + gap`），
 * 空间不足（会越界或压行动区）时**回退到手牌左侧外**（`cards.left − gap`），两条路径都由构造保证与
 * 手牌并集分离 ≥ `gap`（默认 6，= `PROTECTION_POLICY_DEFAULT.heroCardGap`）→ 结构性消灭 143.4px² 相交。
 *
 * 纯几何、无 DOM，可单测；返回气泡 OBB（rot=0）。`carrier` 反映最终承载侧（诊断/aria 用）。
 */
export function placeHeroActionBubble(
  heroPod: OBB,
  heroCards: OBB[],
  box: Size,
  opts?: { size?: Size; gap?: number; footTop?: number },
): { obb: OBB; carrier: 'below' | 'left' } {
  const gap = opts?.gap ?? 6;
  const size = opts?.size ?? { w: 92, h: 20 };
  const hw = size.w / 2;
  const hh = size.h / 2;
  // 无手牌（观战/未发牌）：退化贴 pod 下沿居中。
  if (!heroCards || heroCards.length === 0) {
    const cy = Math.min(box.h - hh, heroPod.cy + heroPod.hh + gap + hh);
    return { obb: obbAt(heroPod.cx, cy, hw, hh, 0), carrier: 'below' };
  }
  const u = unionOf(heroCards);
  // ① 手牌下方（死区）：水平对齐 pod 中心，夹紧在内容盒内。
  const belowCy = u.bottom + gap + hh;
  const footTop = opts?.footTop ?? box.h;
  const cxClamped = Math.min(box.w - hw, Math.max(hw, heroPod.cx));
  if (belowCy + hh <= Math.min(box.h, footTop) + EPS) {
    return { obb: obbAt(cxClamped, belowCy, hw, hh, 0), carrier: 'below' };
  }
  // ② 手牌左侧外：垂直对齐手牌并集中线，右缘 = 手牌左缘 − gap。
  const leftCx = u.left - gap - hw;
  const midCy = (u.top + u.bottom) / 2;
  const cyClamped = Math.min(box.h - hh, Math.max(hh, midCy));
  const cxLeftClamped = Math.max(hw, leftCx);
  return { obb: obbAt(cxLeftClamped, cyClamped, hw, hh, 0), carrier: 'left' };
}

/**
 * 浮层「让位不提层」（设计 §4.6.4 / §4.5，Round21-M1 · AC-B6）。
 *
 * 现状展开态赢率壳/结算等浮层落在公共牌带上 → **100% 覆盖 5 张公共牌（单槽 1056px²）**。设计定稿：
 * 浮层只允许落在 `freeBand`（公共牌带底沿之下 → 英雄区顶沿之上），冲突时**让位**（回落到收起态/下移），
 * 不靠提高 z-index 盖上去。本函数把任一与「公共牌带 ⊕ 呼吸带」相交的 panel **下移到带底之下** `gap` 处
 * （超出内容盒则夹到底部并从顶部收缩），由构造保证与所有 boardSlot 分离 → 结构性消灭 1056px² 覆盖。
 *
 * 纯几何、无 DOM；`boardSlots` 为空时原样返回（无公共牌可让）。
 */
export function resolvePanelPlacement(panels: OBB[], boardSlots: OBB[], box: Size, gap = 8): OBB[] {
  if (!boardSlots || boardSlots.length === 0) return panels;
  const b = unionOf(boardSlots);
  const bandBottom = b.bottom + gap; // 公共牌带底沿 + 呼吸带
  const bandTop = b.top - gap;
  return panels.map((p) => {
    const pa = aabbOf(p);
    const pTop = pa.cy - pa.hh;
    const pBottom = pa.cy + pa.hh;
    // 与「公共牌带 ⊕ gap」在纵向无交叠 + 横向无交叠 → 不冲突，原样保留。
    const vertOverlap = pBottom > bandTop && pTop < bandBottom;
    const horizOverlap = pa.cx + pa.hw > b.left - gap && pa.cx - pa.hw < b.right + gap;
    if (!vertOverlap || !horizOverlap) return p;
    // 让位：把 panel 顶沿钉到带底之下 gap；越界则夹到底部、从顶部收缩高度。
    let newTop = bandBottom;
    let newBottom = newTop + 2 * pa.hh;
    if (newBottom > box.h) {
      newBottom = box.h;
      newTop = Math.min(newTop, Math.max(bandBottom, newBottom - 2 * pa.hh));
      if (newTop < bandBottom) newTop = bandBottom;
    }
    const newHh = Math.max(0, (newBottom - newTop) / 2);
    return obbAt(pa.cx, (newTop + newBottom) / 2, pa.hw, newHh, 0);
  });
}

export function rotOf(innerSide: 'left' | 'right' | 'bottom'): number {
  // 左列（inner='right'，pod 在屏幕左侧）牌背 +6°；右列（inner='left'）−6°；顶弧 0。
  if (innerSide === 'right') return CARD_ROT;
  if (innerSide === 'left') return -CARD_ROT;
  return 0;
}

/** 该席位 pod 外缘到底池的净射线长（越短空间越紧张，越先占）。 */
export function radialFree(s: SeatRuntime, P: Point): number {
  const u = unit(P.x - s.pod.cx, P.y - s.pod.cy);
  const len = Math.hypot(P.x - s.pod.cx, P.y - s.pod.cy);
  return len - proj(s.pod.hw, s.pod.hh, u);
}

// ---------------------------------------------------------------------------
// Round21-M2 · 承载模式 carrierOf（设计 §4.1，T1 归属重构总钥匙）
// ---------------------------------------------------------------------------
//
// 归属由**构造**保证，不由优化保证：对每席位按实测的径向净走廊 `R_free` / 切向背离净空 `T_free`
// 确定性返回 radial|tangential|inPod|converge 三（四）种承载模式，三种模式的 κ 均由几何构造满足
// （radial ≤0.55 / tangential ≤0.50 / inPod ≤0.35 ≤ κ_hard 0.6），不是「优化到勉强合格」。

export type CarrierMode = 'radial' | 'tangential' | 'inPod' | 'converge';

/** 承载模式判定的 kind 归一（cardback/chip/pill/merged/badge → 取尺寸）。 */
export type CarrierKind = 'cardback' | 'chip' | 'pill' | 'merged' | 'badge';

function sizeOfKind(kind: CarrierKind, sizes: SatelliteSizeTable): Size {
  switch (kind) {
    case 'cardback':
      return sizes.cardback;
    case 'chip':
      return sizes.chip;
    case 'pill':
      return sizes.pill;
    case 'merged':
      return sizes.merged;
    case 'badge':
      return sizes.badge;
  }
}

/** 卫星盒沿单位方向 dir 的支撑半径 sup(kind, dir)（设计 §4.1.2 needR/needT 用）。 */
export function supRadius(size: Size, dir: Point): number {
  return proj(size.w / 2, size.h / 2, dir);
}

/**
 * ① 径向净走廊 R_free(i)（设计 §4.1.1）：pod 外缘 → 中心带保护边界，沿 u_i = (P − S_i)/|…|。
 * `R_free = |P − S_i| − proj(pod, u) − bandSupport(u) − centerBandGap`。
 * `bandSupport(u)` = 中心带（以 P 为中心的包络 OBB）沿 u 的支撑半径。
 */
export function radialCorridor(
  pod: OBB,
  P: Point,
  band: { top: number; bottom: number; halfWidth: number },
  centerBandGap: number,
): number {
  const u = unit(P.x - pod.cx, P.y - pod.cy);
  const dist = Math.hypot(P.x - pod.cx, P.y - pod.cy);
  const bandHalfH = (band.bottom - band.top) / 2;
  const bandSupport = proj(band.halfWidth, bandHalfH, u);
  return dist - proj(pod.hw, pod.hh, u) - bandSupport - centerBandGap;
}

/**
 * ② 切向背离净空 T_free(i)（设计 §4.1.1）：pod 外缘 → 最近同区邻座 pod 外缘，取「背离邻座」一侧。
 * `T_free = max(gapToPrev, gapToNext) − S_min`。邻座列表为同区（同为侧列或同为顶弧）相邻 pod 中心。
 * 无邻座（该区仅 1 个 pod）时净空视为 +∞（返回一个大值）。
 */
export function tangentialCorridor(pod: OBB, neighbors: OBB[], sMin: number): number {
  if (neighbors.length === 0) return Number.POSITIVE_INFINITY;
  let maxGap = -Infinity;
  for (const nb of neighbors) {
    // 两 pod 中心距 − 两者切向半宽 ≈ 外缘净空（保守用中心距的一半近似切向可用带）。
    const d = Math.hypot(nb.cx - pod.cx, nb.cy - pod.cy);
    const gap = d - (pod.hw + nb.hw); // 近似外缘间距（同尺寸列，取 hw 保守）
    maxGap = Math.max(maxGap, gap);
  }
  return maxGap - sMin;
}

/**
 * 承载模式判定（设计 §4.1.2，唯一入口，确定性）：
 * ```
 * needR = g0 + sup(kind, u)
 * needT = 2·sup(kind, t) + S_min
 * R_free ≥ needR              → 'radial'
 * T_free ≥ needT              → 'tangential'
 * podSlotAvailable            → 'inPod'
 * else                        → 'converge'
 * ```
 */
export function carrierOf(
  kind: CarrierKind,
  u: Point,
  t: Point,
  ctx: {
    rFree: number;
    tFree: number;
    gaps: GapTable;
    sizes: SatelliteSizeTable;
    podSlotAvailable: boolean;
    sMin?: number;
  },
): CarrierMode {
  const size = sizeOfKind(kind, ctx.sizes);
  const sMin = ctx.sMin ?? ATTRIBUTION_POLICY_DEFAULT.sMinCrossOwner;
  const needR = ctx.gaps.g0 + supRadius(size, u);
  const needT = 2 * supRadius(size, t) + sMin;
  if (ctx.rFree >= needR) return 'radial';
  if (ctx.tFree >= needT) return 'tangential';
  if (ctx.podSlotAvailable) return 'inPod';
  return 'converge';
}

// ---------------------------------------------------------------------------
// Round21-M2 · 归属度实测量（assertAttribution 与 cluster 落位共用单一真源）
// ---------------------------------------------------------------------------

export interface AttributionMeasure {
  /** 卫星到主人 pod 外缘的间隙（SAT 分离；inPod/贴靠时为 0）。 */
  edgeGapOwn: number;
  /** 卫星中心 → 主人 pod 中心距。 */
  dOwn: number;
  /** 卫星中心 → 最近**其他**（非主人、非淘汰）pod 中心距。 */
  dNearestOther: number;
  /** 归属度 κ = dOwn / dNearestOther（无其他 pod 时为 0）。 */
  kappa: number;
  /** 最近其他 pod 的 seat（诊断）。 */
  nearestOtherSeat: number | null;
}

/**
 * 度量单个卫星 OBB 相对其主人 pod 与其他 pod 的归属量（纯几何，实测口径）。
 * `owner` = 主人 pod OBB；`others` = 其他在局 pod（已排除主人与淘汰席）。
 */
export function measureAttribution(sat: OBB, owner: OBB, others: OBB[]): AttributionMeasure {
  const dOwn = Math.hypot(sat.cx - owner.cx, sat.cy - owner.cy);
  const sepOwn = obbSeparation(sat, owner);
  const edgeGapOwn = sepOwn.overlap ? 0 : Math.max(0, dOwn - proj(owner.hw, owner.hh, unit(owner.cx - sat.cx, owner.cy - sat.cy)) - proj(sat.hw, sat.hh, unit(sat.cx - owner.cx, sat.cy - owner.cy)));
  let dNearestOther = Number.POSITIVE_INFINITY;
  let nearestOtherSeat: number | null = null;
  for (let i = 0; i < others.length; i++) {
    const o = others[i];
    const d = Math.hypot(sat.cx - o.cx, sat.cy - o.cy);
    if (d < dNearestOther) {
      dNearestOther = d;
      nearestOtherSeat = i;
    }
  }
  const kappa = Number.isFinite(dNearestOther) && dNearestOther > 0 ? dOwn / dNearestOther : 0;
  return { edgeGapOwn, dOwn, dNearestOther, kappa, nearestOtherSeat };
}

export type { AttributionPolicy };


function growObb(o: OBB, by: number): OBB {
  return { ...o, hw: o.hw + by, hh: o.hh + by };
}

/** 下一档（更小）尺寸（D1 缩档：不换 pod 档，只缩卫星盒）。 */
function nextSize(density: SeatDensity, kind: 'chip' | 'pill'): Size {
  const order: SeatDensity[] = ['full', 'compact', 'row', 'micro'];
  const idx = order.indexOf(density);
  const next = order[Math.min(order.length - 1, idx + 1)];
  return SAT_SIZES[next][kind];
}

interface PlaceResult {
  el?: LayoutElement;
  farR: number; // 放置元素外缘到 pod 中心的半径（供链式外推）；未放置时为 0
}

/** 单位向量绕原点旋转 a（顺时针为正，与层坐标一致）。 */
function rotateVec(v: Point, a: number): Point {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
}

/**
 * 卫星放置（设计 §3.2.2 的角向+径向可行域扫描）：以底池方向 u 为理想方向，绕 pod 朝底池半弧
 * 角向扫掠（0→±90°），并**由近及远**径向平移。距离为外层、角度为内层 —— 优先取「离 pod 最近」
 * 的落点，同距离再取「最贴理想方向」的角度。既保证长射线（顶弧）直落理想槽，又让短射线（侧列/
 * 角位 pod→底池射线穿过中心带者）就近绕开中心带贴 pod 引出，绝不飞到对角、绝不叠压。命中即返回
 * 外缘半径供链式外推。
 */
function placeSatellite(
  s: SeatRuntime,
  u: Point,
  baseD: number,
  hw: number,
  hh: number,
  rot: number,
  occ: Occupancy,
  meta: Omit<LayoutElement, 'obb'>,
): PlaceResult {
  const S = s.pod;
  // 角度阶梯：先理想方向，再对称向两侧扩张（15° 步进到 90°）。
  const angleSteps = [0, 0.26, 0.52, 0.79, 1.05, 1.31, 1.57];
  const angles: number[] = [0];
  for (let i = 1; i < angleSteps.length; i++) {
    angles.push(angleSteps[i], -angleSteps[i]);
  }
  const podReach = Math.max(S.hw, S.hh);
  const maxD = baseD + podReach + 220;
  // 距离外层（由近及远）、角度内层（先理想方向）：优先最近落点，牢牢贴住本 pod。
  for (let d = baseD; d <= maxD; d += 2) {
    for (const a of angles) {
      const dir = rotateVec(u, a);
      const cx = S.cx + dir.x * d;
      const cy = S.cy + dir.y * d;
      const cand: LayoutElement = { ...meta, obb: obbAt(cx, cy, hw, hh, rot) };
      if (occ.freeFor(cand)) {
        occ.add(cand);
        const farR = Math.hypot(cx - S.cx, cy - S.cy) + proj(hw, hh, u);
        return { el: cand, farR };
      }
    }
  }
  return { farR: 0 };
}

/** 放置 SB/BB/D badge 于 pod 角槽（切向沉向桌心）。SB/BB 与 D 分列两角。 */
function placeBadges(
  s: SeatRuntime,
  u: Point,
  t: Point,
  sizes: SatelliteSizeTable,
  occ: Occupancy,
  elements: LayoutElement[],
  degradations: string[],
): void {
  const badges: { role: 'SB' | 'BB' | 'D'; size: Size; tSign: 1 | -1 }[] = [];
  if (s.blind === 'SB') badges.push({ role: 'SB', size: sizes.badge, tSign: 1 });
  if (s.blind === 'BB') badges.push({ role: 'BB', size: sizes.badge, tSign: 1 });
  if (s.isDealer) badges.push({ role: 'D', size: sizes.dealer, tSign: -1 });

  for (const b of badges) {
    const bhw = b.size.w / 2;
    const bhh = b.size.h / 2;
    const podTan = proj(s.pod.hw, s.pod.hh, t);
    const tOff = b.tSign * (podTan + Math.max(bhw, bhh) + 2);
    // 径向略向底池方向（沿 u 半个 pod 支撑半径），保证贴 pod 下角而非压中心。
    const dRad = proj(s.pod.hw, s.pod.hh, u) * 0.5;
    let placed = false;
    for (let step = 0; step <= 4 && !placed; step++) {
      const to = tOff + b.tSign * step * (Math.max(bhw, bhh) + 2);
      const cx = s.pod.cx + u.x * dRad + t.x * to;
      const cy = s.pod.cy + u.y * dRad + t.y * to;
      const cand: LayoutElement = {
        id: `${b.role === 'D' ? 'dealer' : `seat${s.seat}.badge.${b.role}`}`,
        kind: 'badge',
        seat: s.seat,
        role: b.role,
        obb: obbAt(cx, cy, bhw, bhh, 0),
        z: Z.seatBadge,
      };
      if (occ.freeFor(cand)) {
        occ.add(cand);
        elements.push(cand);
        placed = true;
      }
    }
    if (!placed) degradations.push(`${b.role === 'D' ? 'dealer' : `seat${s.seat}.badge.${b.role}`}:hidden`);
  }
}
